import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SiteContext = createContext(null);

// Default content
const defaultContent = {
  hero_title: "Justin Michael La Vallee",
  hero_subtitle: "Composer",
  hero_tagline: "Creating music that moves the soul",
  hero_image: "",
  about_title: "About",
  about_text: "Justin Michael La Vallee is a composer whose work spans film scores, orchestral pieces, and contemporary compositions. With a passion for storytelling through music, Justin creates evocative soundscapes that resonate deeply with audiences.\n\nHis compositions blend classical traditions with modern sensibilities, resulting in music that is both timeless and fresh. Whether scoring for film, television, or concert hall, Justin brings a unique voice and emotional depth to every project.",
  about_image: "",
  contact_title: "Get In Touch",
  contact_text: "For inquiries about commissions, collaborations, or licensing, please reach out."
};

export function SiteProvider({ children }) {
  const [content, setContent] = useState(defaultContent);
  const [playlists, setPlaylists] = useState([]);
  const [videos, setVideos] = useState([]);
  const [isAdmin, setIsAdmin] = useState(() => {
    // Check for stored credentials on initial load
    const stored = sessionStorage.getItem('adminCredentials');
    return !!stored;
  });
  const [adminCredentials, setAdminCredentials] = useState(() => {
    const stored = sessionStorage.getItem('adminCredentials');
    return stored ? JSON.parse(stored) : null;
  });
  const [loading, setLoading] = useState(true);

  // Fetch public content
  const fetchContent = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/content`);
      if (response.ok) {
        const data = await response.json();
        // Merge defaults with API data - API data takes precedence
        setContent({ ...defaultContent, ...data });
      }
    } catch (error) {
      console.error('Error fetching content:', error);
    }
  }, []);

  const fetchPlaylists = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/playlists`);
      if (response.ok) {
        const data = await response.json();
        setPlaylists(data);
      }
    } catch (error) {
      console.error('Error fetching playlists:', error);
    }
  }, []);

  const fetchVideos = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/videos`);
      if (response.ok) {
        const data = await response.json();
        setVideos(data);
      }
    } catch (error) {
      console.error('Error fetching videos:', error);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchContent(), fetchPlaylists(), fetchVideos()]);
      setLoading(false);
    };
    init();
  }, [fetchContent, fetchPlaylists, fetchVideos]);

  // Admin login
  const login = useCallback(async (username, password) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/verify`, {
        headers: {
          'Authorization': 'Basic ' + btoa(`${username}:${password}`)
        }
      });
      if (response.ok) {
        setIsAdmin(true);
        setAdminCredentials({ username, password });
        // Persist to sessionStorage
        sessionStorage.setItem('adminCredentials', JSON.stringify({ username, password }));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  }, []);

  const logout = useCallback(() => {
    setIsAdmin(false);
    setAdminCredentials(null);
    sessionStorage.removeItem('adminCredentials');
  }, []);

  // Helper for admin API calls
  const adminFetch = useCallback(async (url, options = {}) => {
    if (!adminCredentials) throw new Error('Not authenticated');
    
    const headers = {
      ...options.headers,
      'Authorization': 'Basic ' + btoa(`${adminCredentials.username}:${adminCredentials.password}`),
      'Content-Type': 'application/json',
    };
    
    return fetch(`${API_URL}${url}`, { ...options, headers });
  }, [adminCredentials]);

  // Admin actions
  const updateContent = useCallback(async (updates) => {
    const response = await adminFetch('/api/admin/content', {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
    if (response.ok) {
      const data = await response.json();
      setContent({ ...defaultContent, ...data });
      return true;
    }
    return false;
  }, [adminFetch]);

  const createPlaylist = useCallback(async (playlist) => {
    const response = await adminFetch('/api/admin/playlists', {
      method: 'POST',
      body: JSON.stringify(playlist)
    });
    if (response.ok) {
      const data = await response.json();
      setPlaylists(prev => [...prev, data]);
      return data;
    }
    return null;
  }, [adminFetch]);

  const updatePlaylist = useCallback(async (id, updates) => {
    const response = await adminFetch(`/api/admin/playlists/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
    if (response.ok) {
      const data = await response.json();
      setPlaylists(prev => prev.map(p => p.id === id ? data : p));
      return true;
    }
    return false;
  }, [adminFetch]);

  const deletePlaylist = useCallback(async (id) => {
    const response = await adminFetch(`/api/admin/playlists/${id}`, {
      method: 'DELETE'
    });
    if (response.ok) {
      setPlaylists(prev => prev.filter(p => p.id !== id));
      return true;
    }
    return false;
  }, [adminFetch]);

  const reorderPlaylists = useCallback(async (playlistIds) => {
    const response = await adminFetch('/api/admin/playlists/reorder', {
      method: 'PUT',
      body: JSON.stringify({ playlist_ids: playlistIds })
    });
    if (response.ok) {
      // Reorder local state to match
      setPlaylists(prev => {
        const ordered = [];
        for (const id of playlistIds) {
          const playlist = prev.find(p => p.id === id);
          if (playlist) ordered.push(playlist);
        }
        return ordered;
      });
      return true;
    }
    return false;
  }, [adminFetch]);

  const createVideo = useCallback(async (video) => {
    const response = await adminFetch('/api/admin/videos', {
      method: 'POST',
      body: JSON.stringify(video)
    });
    if (response.ok) {
      const data = await response.json();
      setVideos(prev => [...prev, data]);
      return data;
    }
    return null;
  }, [adminFetch]);

  const updateVideo = useCallback(async (id, updates) => {
    const response = await adminFetch(`/api/admin/videos/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
    if (response.ok) {
      const data = await response.json();
      setVideos(prev => prev.map(v => v.id === id ? data : v));
      return true;
    }
    return false;
  }, [adminFetch]);

  const deleteVideo = useCallback(async (id) => {
    const response = await adminFetch(`/api/admin/videos/${id}`, {
      method: 'DELETE'
    });
    if (response.ok) {
      setVideos(prev => prev.filter(v => v.id !== id));
      return true;
    }
    return false;
  }, [adminFetch]);

  const reorderVideos = useCallback(async (videoIds) => {
    const response = await adminFetch('/api/admin/videos/reorder', {
      method: 'PUT',
      body: JSON.stringify({ video_ids: videoIds })
    });
    if (response.ok) {
      setVideos(prev => {
        const ordered = [];
        for (const id of videoIds) {
          const video = prev.find(v => v.id === id);
          if (video) ordered.push(video);
        }
        return ordered;
      });
      return true;
    }
    return false;
  }, [adminFetch]);

  // Contact form
  const sendContactMessage = useCallback(async (message) => {
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(message)
      });
      return response.ok;
    } catch (error) {
      console.error('Error sending message:', error);
      return false;
    }
  }, []);

  const value = {
    content,
    playlists,
    videos,
    loading,
    isAdmin,
    login,
    logout,
    updateContent,
    createPlaylist,
    updatePlaylist,
    deletePlaylist,
    reorderPlaylists,
    createVideo,
    updateVideo,
    deleteVideo,
    reorderVideos,
    sendContactMessage,
    refreshData: () => Promise.all([fetchContent(), fetchPlaylists(), fetchVideos()])
  };

  return (
    <SiteContext.Provider value={value}>
      {children}
    </SiteContext.Provider>
  );
}

export function useSite() {
  const context = useContext(SiteContext);
  if (!context) {
    throw new Error('useSite must be used within a SiteProvider');
  }
  return context;
}
